import React, { useCallback, useMemo } from 'react';
import {
  DefaultContextMenuContent,
  TldrawUiMenuGroup,
  TldrawUiMenuItem,
  useEditor,
  useReadonly,
  useValue,
} from 'tldraw';
import { deleteSelectedCanvasShapes } from '../utils/deleteCanvasPins';
import {
  addConnectLineFromContext,
  addFrameFromContext,
  addTextFromContext,
  connectSelectedShapesFromContext,
  promptAndPasteClipboard,
} from '../utils/canvasEmptySpaceActions';
import { getConnectablePair } from '../utils/connectSelectedShapes';
import { useBoardAiContext } from '../context/BoardAiContext';
import { useBoardStore } from '../store/useBoardStore';
import { createOpsOnlyDocumentSaver } from '../utils/persistBoardDocumentOps';

/** Frame title sits above the body; include that strip plus a small edge margin. */
function pointHitsFrameChrome(editor, frame, point) {
  const bounds = editor.getShapePageBounds?.(frame);
  if (!bounds) return false;
  const zoom = editor.getZoomLevel?.() || 1;
  const titlePad = 36 / zoom;
  const edge = 8 / zoom;
  return (
    point.x >= bounds.x - edge
    && point.x <= bounds.x + bounds.w + edge
    && point.y >= bounds.y - titlePad
    && point.y <= bounds.y + bounds.h + edge
  );
}

/**
 * True when the right-click lands on empty canvas (no shape under the cursor).
 * Multi-select is an exception: tldraw keeps the selection when the click is
 * inside the selection rectangle, even in the gap between pins. That should
 * open the selection menu (Delete), not empty-space actions.
 *
 * Frame bodies are hollow and frame titles are excluded from getShapesAtPoint,
 * so a click on a frame looks empty unless we ask for the frame explicitly.
 */
export function isEmptyCanvasContextMenu(editor) {
  if (!editor) return false;
  if (editor.getSelectedShapeIds().length > 1) return false;
  const point = editor.inputs?.currentPagePoint;
  if (!point) return editor.getSelectedShapeIds().length === 0;
  const shapes = editor.getShapesAtPoint(point) || [];
  if (shapes.length > 0) return false;
  const underPointer = editor.getShapeAtPoint?.(point, {
    hitInside: true,
    hitFrameInside: true,
    hitLabels: true,
    margin: 8,
  });
  if (underPointer?.type === 'frame') return false;
  const only = editor.getOnlySelectedShape?.();
  if (only?.type === 'frame' && pointHitsFrameChrome(editor, only, point)) return false;
  return true;
}

const BoardContextMenuContent = ({ clipboardLabel = null } = {}) => {
  const editor = useEditor();
  const isReadonly = useReadonly();
  const hashtag = useBoardStore(s => s.hashtag);
  const { analyzePinAdded, openBoardChat } = useBoardAiContext();

  const selectedCount = useValue(
    'board-context-menu-selection-count',
    () => editor.getSelectedShapeIds().length,
    [editor]
  );
  const hasSelection = selectedCount > 0;
  const canConnectPair = useValue(
    'board-context-menu-can-connect-pair',
    () => Boolean(getConnectablePair(editor)),
    [editor]
  );

  const isEmptySpace = useValue(
    'board-context-menu-empty-space',
    () => isEmptyCanvasContextMenu(editor),
    [editor]
  );

  const handleAskAi = useCallback(() => {
    if (!hasSelection) return;
    // Open after the menu unmounts so the chat panel can take focus.
    requestAnimationFrame(() => openBoardChat?.());
  }, [hasSelection, openBoardChat]);

  const handleDelete = useCallback(async () => {
    if (!hasSelection || isReadonly) return;
    await deleteSelectedCanvasShapes(editor, { confirmUserPins: true });
  }, [editor, hasSelection, isReadonly]);

  const pagePointAtMenu = useCallback(() => {
    const point = editor.inputs?.currentPagePoint;
    if (point && Number.isFinite(point.x) && Number.isFinite(point.y)) {
      return { x: point.x, y: point.y };
    }
    return { x: 80, y: 80 };
  }, [editor]);

  const persistDocument = useMemo(() => createOpsOnlyDocumentSaver(editor), [editor]);

  const handlePasteClipboard = useCallback(async () => {
    if (isReadonly) return;
    await promptAndPasteClipboard(editor, pagePointAtMenu(), {
      hashtag,
      saveSnapshot: persistDocument,
      analyzePinAdded,
    });
  }, [analyzePinAdded, editor, hashtag, isReadonly, pagePointAtMenu, persistDocument]);

  const handleAddText = useCallback(() => {
    if (isReadonly) return;
    addTextFromContext(editor, pagePointAtMenu(), {
      saveSnapshot: persistDocument,
    });
  }, [editor, isReadonly, pagePointAtMenu, persistDocument]);

  const handleAddFrame = useCallback(() => {
    if (isReadonly) return;
    addFrameFromContext(editor, pagePointAtMenu(), {
      saveSnapshot: persistDocument,
    });
  }, [editor, isReadonly, pagePointAtMenu, persistDocument]);

  const handleAddConnectLine = useCallback(async () => {
    if (isReadonly) return;
    await addConnectLineFromContext(editor, pagePointAtMenu(), {
      saveSnapshot: persistDocument,
    });
  }, [editor, isReadonly, pagePointAtMenu, persistDocument]);

  const handleConnectSelection = useCallback(async () => {
    if (isReadonly) return;
    await connectSelectedShapesFromContext(editor, {
      saveSnapshot: persistDocument,
    });
  }, [editor, isReadonly, persistDocument]);

  if (isEmptySpace) {
    if (isReadonly) {
      return <DefaultContextMenuContent />;
    }
    return (
      <TldrawUiMenuGroup id="canvas-empty-actions">
        {clipboardLabel ? (
          <TldrawUiMenuItem
            id="paste-clipboard"
            label={clipboardLabel}
            iconLeft="clipboard-copy"
            readonlyOk={false}
            onSelect={handlePasteClipboard}
          />
        ) : null}
        <TldrawUiMenuItem
          id="add-text"
          label="Add thought"
          iconLeft="tool-text"
          readonlyOk={false}
          onSelect={handleAddText}
        />
        <TldrawUiMenuItem
          id="add-frame"
          label="Add group frame"
          iconLeft="tool-frame"
          readonlyOk={false}
          onSelect={handleAddFrame}
        />
        <TldrawUiMenuItem
          id="add-connect-line"
          label="Add connect line"
          iconLeft="tool-arrow"
          readonlyOk={false}
          onSelect={handleAddConnectLine}
        />
      </TldrawUiMenuGroup>
    );
  }

  if (!hasSelection) {
    return <DefaultContextMenuContent />;
  }

  return (
    <TldrawUiMenuGroup id="canvas-actions">
      {canConnectPair && !isReadonly ? (
        <TldrawUiMenuItem
          id="connect-selection"
          label="Connect"
          iconLeft="tool-arrow"
          readonlyOk={false}
          onSelect={handleConnectSelection}
        />
      ) : null}
      <TldrawUiMenuItem
        id="ask-ai"
        label="Ask AI"
        iconLeft="comment"
        onSelect={handleAskAi}
      />
      <TldrawUiMenuItem
        id="delete-selection"
        label="action.delete"
        iconLeft="trash"
        onSelect={handleDelete}
      />
    </TldrawUiMenuGroup>
  );
};

export default BoardContextMenuContent;
